import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver'; // For CSV download
import { IoEye, IoClose } from 'react-icons/io5';

const CompanyBalancePage = () => {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState({});
  const [modalData, setModalData] = useState({ visible: false, companyId: null, filterDate: '', fromDate: '', toDate: '' });
  const [expandedCompany, setExpandedCompany] = useState(null); // Reintroduced state
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


  useEffect(() => {
    fetchBalances();
  }, []);

  const fetchBalances = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company-balances`);
      setBalances(response.data);
    } catch (err) {
      setError('Failed to load balances. Please try again.');
      console.error('Error fetching balances:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (companyId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/orders?companyId=${companyId}`);
      setTransactions(prev => ({ ...prev, [companyId]: response.data }));
    } catch (err) {
      console.error('Error fetching transactions:', err);
    }
  };

  const toggleExpand = (companyId) => { // Reintroduced function
    if (expandedCompany === companyId) {
      setExpandedCompany(null);
    } else {
      setExpandedCompany(companyId);
      if (!transactions[companyId]) {
        fetchTransactions(companyId);
      }
    }
  };

  const openModal = (companyId) => {
    if (!transactions[companyId]) fetchTransactions(companyId);
    setModalData({ visible: true, companyId, filterDate: '', fromDate: '', toDate: '' });
  };

  const closeModal = () => setModalData({ visible: false, companyId: null, filterDate: '', fromDate: '', toDate: '' });

  const filterTransactions = (trans, filterDate, fromDate, toDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    if (fromDate && toDate) {
      const start = new Date(fromDate);
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);
      return trans.filter(t => new Date(t.ORDER_DATE) >= start && new Date(t.ORDER_DATE) <= end);
    }

    if (filterDate) {
      const date = new Date(filterDate);
      date.setHours(0, 0, 0, 0);
      const dateEnd = new Date(date);
      dateEnd.setHours(23, 59, 59, 999);

      switch (true) {
        case filterDate === 'day':
          return trans.filter(t => {
            const transDate = new Date(t.ORDER_DATE);
            return transDate >= today && transDate <= new Date(today.setHours(23, 59, 59, 999));
          });
        case filterDate === 'week':
          return trans.filter(t => new Date(t.ORDER_DATE) >= weekAgo);
        case filterDate === 'month':
          return trans.filter(t => new Date(t.ORDER_DATE) >= monthStart);
        default:
          return trans.filter(t => {
            const transDate = new Date(t.ORDER_DATE);
            return transDate >= date && transDate <= dateEnd;
          });
      }
    }
    return trans;
  };

  const downloadCSV = (companyId) => {
    const balance = balances.find(b => b.COMPANY_ID === companyId);
    const trans = filterTransactions(transactions[companyId] || [], modalData.filterDate, modalData.fromDate, modalData.toDate);
    const csv = [
      'Order ID,Date,Points Used,Starting Balance,Current Balance',
      ...trans.map(t => [
        t.ORDER_ID,
        new Date(t.ORDER_DATE).toLocaleDateString(),
        Number(t.POINTS_PAYMENT_AMOUNT || 0).toFixed(2),
        (balance?.startingBalance || 0).toFixed(2),
        (balance?.currentBalance || 0).toFixed(2),
      ].join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `transactions_company_${companyId}.csv`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 text-gray-800">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Company Balances</h2>
      {loading && <p className="text-gray-600">Loading...</p>}
      {error && <p className="text-red-600">{error}</p>}
      {!loading && !error && (
        <table className="w-full text-left border-separate border-spacing-0">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-3 text-gray-800 font-semibold">Company Name</th>
              <th className="p-3 text-gray-800 font-semibold">Starting Balance</th>
              <th className="p-3 text-gray-800 font-semibold">Current Balance</th>
              <th className="p-3 text-gray-800 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {balances.map((balance) => (
              <React.Fragment key={balance.COMPANY_ID}>
                <tr className="border-t border-gray-300 hover:bg-gray-100 transition-colors">
                  <td className="p-3 text-gray-800">
                    <span
                      className="cursor-pointer mr-2"
                      onClick={() => toggleExpand(balance.COMPANY_ID)}
                    >
                      {expandedCompany === balance.COMPANY_ID ? '▼' : '▶'}
                    </span>
                    {balance.COMPANY_NAME || 'Unknown'}
                  </td>
                  <td className="p-3 text-gray-800">{(balance.startingBalance || 0).toFixed(2)}</td>
                  <td className="p-3 text-gray-800" style={{ color: (balance.currentBalance || 0) < 0 ? 'red' : 'inherit' }}>
                    {(balance.currentBalance || 0).toFixed(2)}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => openModal(balance.COMPANY_ID)}
                      className="flex items-center bg-teal-600 text-white px-3 py-1 rounded-lg hover:bg-teal-700 transition-colors"
                    >
                      <IoEye className="mr-1" /> View
                    </button>
                  </td>
                </tr>
                {expandedCompany === balance.COMPANY_ID && (
                  <tr>
                    <td colSpan="4" className="p-3">
                      <div className="bg-gray-100 p-3 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800">Transactions</h3>
                        {transactions[balance.COMPANY_ID] ? (
                          transactions[balance.COMPANY_ID].length > 0 ? (
                            <table className="w-full text-left mt-2">
                              <thead>
                                <tr className="bg-gray-200">
                                  <th className="p-2 text-gray-800">Order ID</th>
                                  <th className="p-2 text-gray-800">Date</th>
                                  <th className="p-2 text-gray-800">Points Used</th>
                                </tr>
                              </thead>
                              <tbody>
                                {transactions[balance.COMPANY_ID].map((trans) => (
                                  <tr key={trans.ORDER_ID} className="border-t border-gray-300">
                                    <td className="p-2 text-gray-800">{trans.ORDER_ID}</td>
                                    <td className="p-2 text-gray-800">{new Date(trans.ORDER_DATE).toLocaleDateString()}</td>
                                    <td className="p-2 text-gray-800">{Number(trans.POINTS_PAYMENT_AMOUNT || 0).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : <p className="text-gray-800">No transactions found.</p>
                        ) : <p className="text-gray-800">Loading transactions...</p>}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal */}
      {modalData.visible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl relative overflow-y-auto max-h-[80vh]">
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
            >
              <IoClose size={24} />
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">
              Transactions for {balances.find(b => b.COMPANY_ID === modalData.companyId)?.COMPANY_NAME || 'Unknown'}
            </h2>
            <div className="mb-4 flex flex-wrap gap-4 items-center">
              <input
                type="date"
                value={modalData.filterDate}
                onChange={(e) => setModalData(prev => ({ ...prev, filterDate: e.target.value, fromDate: '', toDate: '' }))}
                className="p-2 border border-gray-300 rounded-lg text-gray-800"
              />
              <select
                value={modalData.filterDate}
                onChange={(e) => setModalData(prev => ({ ...prev, filterDate: e.target.value, fromDate: '', toDate: '' }))}
                className="p-2 border border-gray-300 rounded-lg text-gray-800"
              >
                <option value="">All Transactions</option>
                <option value="day">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">This Month</option>
              </select>
              <input
                type="date"
                value={modalData.fromDate}
                onChange={(e) => setModalData(prev => ({ ...prev, fromDate: e.target.value, filterDate: '', toDate: modalData.toDate }))}
                className="p-2 border border-gray-300 rounded-lg text-gray-800"
                placeholder="From"
              />
              <input
                type="date"
                value={modalData.toDate}
                onChange={(e) => setModalData(prev => ({ ...prev, toDate: e.target.value, filterDate: '', fromDate: modalData.fromDate }))}
                className="p-2 border border-gray-300 rounded-lg text-gray-800"
                placeholder="To"
              />
              <button
                onClick={() => downloadCSV(modalData.companyId)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Download CSV
              </button>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {transactions[modalData.companyId] ? (
                filterTransactions(transactions[modalData.companyId], modalData.filterDate, modalData.fromDate, modalData.toDate).length > 0 ? (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-200">
                        <th className="p-2 text-gray-800 font-semibold">Order ID</th>
                        <th className="p-2 text-gray-800 font-semibold">Date</th>
                        <th className="p-2 text-gray-800 font-semibold">Points Used</th>
                        <th className="p-2 text-gray-800 font-semibold">Starting Balance</th>
                        <th className="p-2 text-gray-800 font-semibold">Current Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterTransactions(transactions[modalData.companyId], modalData.filterDate, modalData.fromDate, modalData.toDate).map((trans) => {
                        const balance = balances.find(b => b.COMPANY_ID === modalData.companyId);
                        return (
                          <tr key={trans.ORDER_ID} className="border-t border-gray-300 hover:bg-gray-100">
                            <td className="p-2 text-gray-800">{trans.ORDER_ID}</td>
                            <td className="p-2 text-gray-800">{new Date(trans.ORDER_DATE).toLocaleDateString()}</td>
                            <td className="p-2 text-gray-800">{Number(trans.POINTS_PAYMENT_AMOUNT || 0).toFixed(2)}</td>
                            <td className="p-2 text-gray-800">{(balance?.startingBalance || 0).toFixed(2)}</td>
                            <td className="p-2 text-gray-800" style={{ color: (balance?.currentBalance || 0) < 0 ? 'red' : 'inherit' }}>
                              {(balance?.currentBalance || 0).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : <p className="text-gray-800">No transactions found for the selected filter.</p>
              ) : <p className="text-gray-800">Loading transactions...</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyBalancePage;